import { Fragment, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { PeriodRow } from "../types";
import {
  getBankingContractBlocks,
  getBankingContractReturnBlocks,
  getBankingCumulativeBlocks,
  getBankingCumulativeReturnBlocks,
} from "../../../../Rest_api/RestApi";
import { CUMULATIVE_ID } from "./ContractsSidebar";
import { BankingContractAnalyticsBucket } from "../../../../models/banking";
import { formatBucketLabel } from "../helpers";
import { Granularity } from "../types";

function groupBlocks(
  blocks: BankingContractAnalyticsBucket[],
): BankingContractAnalyticsBucket[] {
  const grouped = new Map<number, BankingContractAnalyticsBucket>();
  for (const b of blocks) {
    const blockNum = b.time_block ?? 0;
    const existing = grouped.get(blockNum);
    if (existing) {
      existing.scheduled_mwh =
        (existing.scheduled_mwh ?? 0) + (b.scheduled_mwh ?? 0);
      existing.actual_mwh = (existing.actual_mwh ?? 0) + (b.actual_mwh ?? 0);
    } else {
      grouped.set(blockNum, { ...b });
    }
  }
  return Array.from(grouped.values()).sort((a, b) => {
    if (
      a.time_block !== undefined &&
      b.time_block !== undefined &&
      a.time_block !== b.time_block
    ) {
      return a.time_block - b.time_block;
    }
    const tA = a.time || extractBlockTime(a.date);
    const tB = b.time || extractBlockTime(b.date);
    return tA.localeCompare(tB);
  });
}

function extractBlockTime(raw: string): string {
  if (!raw) return "";
  // Check for ISO T separator
  const tIdx = raw.indexOf("T");
  if (tIdx !== -1) {
    const timePart = raw.slice(tIdx + 1, tIdx + 6);
    if (timePart.includes(":")) return timePart;
  }
  // Check for space separator
  const spaceIdx = raw.indexOf(" ");
  if (spaceIdx !== -1) {
    const timePart = raw.slice(spaceIdx + 1, spaceIdx + 6);
    if (timePart.includes(":")) return timePart;
  }
  // Check if the whole string is just HH:mm
  if (raw.includes(":") && raw.length <= 8) return raw.slice(0, 5);
  return "";
}

export function PeriodBreakdownTable({
  rows,
  contractId,
  enableExpand = true,
  mode = "supply",
  title = "Period breakdown",
  preloadedBlocks,
  granularity = "Day",
  showApprovalNos: showApprovalNosProp,
  showCategories: showCategoriesProp,
}: {
  rows: PeriodRow[];
  contractId: string;
  enableExpand?: boolean;
  mode?: "supply" | "return";
  title?: string;
  preloadedBlocks?: Record<string, BankingContractAnalyticsBucket[]>;
  granularity?: Granularity;
  showApprovalNos?: boolean;
  showCategories?: boolean;
}) {
  const hasApprovalNos = showApprovalNosProp ?? rows.some((r) => (r.approval_nos ?? []).length > 0);
  const hasCategories = showCategoriesProp ?? rows.some((r) => (r.categories ?? []).length > 0);
  const totalCols = 5 + (hasApprovalNos ? 1 : 0) + (hasCategories ? 1 : 0);

  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const [blockCache, setBlockCache] = useState<
    Record<string, BankingContractAnalyticsBucket[]>
  >(() => {
    if (!preloadedBlocks) return {};
    const processed: Record<string, BankingContractAnalyticsBucket[]> = {};
    Object.entries(preloadedBlocks).forEach(([date, blocks]) => {
      processed[date] = groupBlocks(blocks);
    });
    return processed;
  });

  useEffect(() => {
    if (!preloadedBlocks) {
      setBlockCache({});
    } else {
      const processed: Record<string, BankingContractAnalyticsBucket[]> = {};
      Object.entries(preloadedBlocks).forEach(([date, blocks]) => {
        processed[date] = groupBlocks(blocks);
      });
      setBlockCache(processed);
    }
    setExpandedDate(null);
  }, [preloadedBlocks, granularity]);
  const [loadingDate, setLoadingDate] = useState<string | null>(null);
  const [errorDate, setErrorDate] = useState<{
    date: string;
    message: string;
  } | null>(null);

  const toggleExpand = async (date: string) => {
    if (expandedDate === date) {
      setExpandedDate(null);
      return;
    }
    setExpandedDate(date);
    setErrorDate(null);
    // blockCache[date] is set (even as []) once we've done a full fetch
    if (date in blockCache) return;
    if (!contractId) return;
    setLoadingDate(date);
    try {
      let data: BankingContractAnalyticsBucket[] = [];
      if (contractId === CUMULATIVE_ID) {
        data = await (mode === "return"
          ? getBankingCumulativeReturnBlocks()
          : getBankingCumulativeBlocks());
      } else {
        data = await (mode === "return"
          ? getBankingContractReturnBlocks(contractId)
          : getBankingContractBlocks(contractId));
      }

      // Group all returned blocks by their date label at the current granularity,
      // so one API call populates every period in the cache at once.
      const byLabel: Record<string, BankingContractAnalyticsBucket[]> = {};
      for (const b of data) {
        const label = formatBucketLabel(b.date, granularity);
        if (!byLabel[label]) byLabel[label] = [];
        byLabel[label].push(b);
      }

      setBlockCache((prev) => {
        const next: Record<string, BankingContractAnalyticsBucket[]> = { ...prev };
        for (const [label, blocks] of Object.entries(byLabel)) {
          next[label] = groupBlocks(blocks);
        }
        // Ensure the expanded date has an entry so re-clicks skip the fetch
        if (!(date in next)) next[date] = [];
        return next;
      });
    } catch (err) {
      setErrorDate({
        date,
        message: (err as Error)?.message ?? "Failed to load blocks",
      });
    } finally {
      setLoadingDate((curr) => (curr === date ? null : curr));
    }
  };

  return (
    <div className="bg-white border border-[#ECEEF3] rounded-[10px] px-4 py-3.5 mb-3.5">
      <div className="flex flex-col gap-0.5 mb-2.5">
        <div className="text-[13px] font-semibold text-[#525062]">
          {title}
        </div>
        {enableExpand && (
          <div className="text-[11px] text-[#8C8A96]">
            Click a day to drill into 15-minute blocks
          </div>
        )}
      </div>
      <div className="overflow-x-auto overflow-y-auto max-h-[480px]">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr>
              <Th align="left">DATE</Th>
              <Th align="right">SCHEDULED MWH</Th>
              <Th align="right">ACTUAL MWH</Th>
              <Th align="right">Δ MWH</Th>
              <Th align="right">Δ %</Th>
              {hasApprovalNos && <Th align="right">APPROVAL NOs.</Th>}
              {hasCategories && <Th align="right">CATEGORIES</Th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isOpen = enableExpand && expandedDate === r.date;
              const isLoading = enableExpand && loadingDate === r.date;
              const blocks = blockCache[r.date] ?? [];
              return (
                <Fragment key={r.date}>
                  <tr
                    onClick={
                      enableExpand ? () => toggleExpand(r.date) : undefined
                    }
                    className={
                      enableExpand ? "cursor-pointer hover:bg-[#FAFBFD]" : ""
                    }
                  >
                    <td className="text-left text-[#1A1B2B] px-3.5 py-2.5 border-b border-[#F4F5F8] tabular-nums">
                      <span className="inline-flex items-center gap-1">
                        {enableExpand &&
                          (isLoading ? (
                            <Loader2
                              size={12}
                              className="text-[#828A99] animate-spin"
                            />
                          ) : isOpen ? (
                            <ChevronDown
                              size={12}
                              className="text-[#828A99]"
                            />
                          ) : (
                            <ChevronRight
                              size={12}
                              className="text-[#C8C6CF]"
                            />
                          ))}
                        {r.label || formatBucketLabel(r.date, granularity)}
                      </span>
                    </td>
                    <td className="text-right text-[#1A1B2B] px-3.5 py-2.5 border-b border-[#F4F5F8] tabular-nums">
                      {r.scheduled.toFixed(2)}
                    </td>
                    <td className="text-right text-[#1A1B2B] px-3.5 py-2.5 border-b border-[#F4F5F8] tabular-nums">
                      {r.actual.toFixed(2)}
                    </td>
                    <td
                      className={`text-right px-3.5 py-2.5 border-b border-[#F4F5F8] tabular-nums ${
                        r.delta < 0 ? "text-[#E0463C]" : "text-[#1F8A39]"
                      }`}
                    >
                      {r.delta.toFixed(2)}
                    </td>
                    <td
                      className={`text-right px-3.5 py-2.5 border-b border-[#F4F5F8] tabular-nums ${
                        r.pct < 0 ? "text-[#E0463C]" : "text-[#1F8A39]"
                      }`}
                    >
                      {r.pct.toFixed(1)}%
                    </td>
                    {hasApprovalNos && (
                      <td className="text-right text-[#525062] px-3.5 py-2.5 border-b border-[#F4F5F8] text-[11px] max-w-[220px]">
                        {(r.approval_nos ?? []).length > 0 ? (
                          <div className="flex flex-col gap-0.5">
                            {(r.approval_nos ?? []).map((a) => (
                              <span key={a} className="break-all leading-tight">{a}</span>
                            ))}
                          </div>
                        ) : null}
                      </td>
                    )}
                    {hasCategories && (
                      <td className="text-right px-3.5 py-2.5 border-b border-[#F4F5F8]">
                        <div className="flex flex-wrap justify-end gap-1">
                          {(r.categories ?? []).map((c) => (
                            <span key={c} className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[#2E6FD9] text-[10px] font-medium whitespace-nowrap">
                              {c}
                            </span>
                          ))}
                        </div>
                      </td>
                    )}
                  </tr>

                  {isOpen && errorDate?.date === r.date && (
                    <tr className="bg-[#FFF5F5]">
                      <td
                        colSpan={totalCols}
                        className="px-3.5 py-2 text-[11px] text-[#E0463C] border-b border-[#F4F5F8]"
                      >
                        {errorDate.message}
                      </td>
                    </tr>
                  )}

                  {isOpen &&
                    !isLoading &&
                    !errorDate &&
                    blocks.length === 0 && (
                      <tr className="bg-[#FAFBFD]">
                        <td
                          colSpan={totalCols}
                          className="px-3.5 py-2 text-[11px] text-[#828A99] border-b border-[#F4F5F8]"
                        >
                          No 15-minute block data for this date.
                        </td>
                      </tr>
                    )}

                  {isOpen &&
                    [...blocks]
                    .sort((a, b) => (a.time_block ?? 0) - (b.time_block ?? 0))
                    .map((b, idx) => {
                      const timeLabel =
                        b.time ?? extractBlockTime(b.date) ?? b.date;
                      const blockNumber = b.time_block ?? idx + 1;
                      return (
                      <tr
                        key={`${r.date}-${b.time_block ?? idx}`}
                        className="bg-[#FAFBFD]"
                      >
                        <td className="text-left text-[#525062] pl-9 pr-3.5 py-2 border-b border-[#F4F5F8] tabular-nums text-[11px]">
                          <span className="inline-flex items-center gap-2">
                            <span className="inline-flex items-center justify-center min-w-[26px] h-[18px] px-1.5 rounded-md bg-[#ECEEF3] text-[#525062] font-semibold text-[10px]">
                              {blockNumber}
                            </span>
                            <span className="text-[#828A99]">{timeLabel || `--:--`}</span>
                          </span>
                        </td>
                        <td className="text-right text-[#525062] px-3.5 py-2 border-b border-[#F4F5F8] tabular-nums text-[11px]">
                          {b.scheduled_mwh.toFixed(2)}
                        </td>
                        <td className="text-right text-[#525062] px-3.5 py-2 border-b border-[#F4F5F8] tabular-nums text-[11px]">
                          {b.actual_mwh.toFixed(2)}
                        </td>
                        <td
                          className={`text-right px-3.5 py-2 border-b border-[#F4F5F8] tabular-nums text-[11px] ${
                            b.delta_mwh < 0
                              ? "text-[#E0463C]"
                              : "text-[#1F8A39]"
                          }`}
                        >
                          {b.delta_mwh.toFixed(2)}
                        </td>
                        <td
                          className={`text-right px-3.5 py-2 border-b border-[#F4F5F8] tabular-nums text-[11px] ${
                            b.delta_pct < 0
                              ? "text-[#E0463C]"
                              : "text-[#1F8A39]"
                          }`}
                        >
                          {b.delta_pct.toFixed(1)}%
                        </td>
                        {hasApprovalNos && (
                          <td className="text-right text-[#525062] px-3.5 py-2 border-b border-[#F4F5F8] text-[11px] max-w-[220px]">
                            {(b.approval_nos ?? []).length > 0 ? (
                              <div className="flex flex-col gap-0.5">
                                {(b.approval_nos ?? []).map((a) => (
                                  <span key={a} className="break-all leading-tight">{a}</span>
                                ))}
                              </div>
                            ) : null}
                          </td>
                        )}
                        {hasCategories && (
                          <td className="text-right px-3.5 py-2 border-b border-[#F4F5F8]">
                            <div className="flex flex-wrap justify-end gap-1">
                              {(b.categories ?? []).map((c) => (
                                <span key={c} className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[#2E6FD9] text-[10px] font-medium whitespace-nowrap">
                                  {c}
                                </span>
                              ))}
                            </div>
                          </td>
                        )}
                      </tr>
                      );                    })}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({
  children,
  align,
}: {
  children: React.ReactNode;
  align: "left" | "right" | "center";
}) {
  return (
    <th
      className={`${
        align === "left" ? "text-left" : "text-right"
      } sticky top-0 z-10 bg-white font-semibold text-[10px] tracking-wider text-[#8C8A96] px-3.5 py-2.5 border-b border-[#ECEEF3]`}
    >
      {children}
    </th>
  );
}

export default PeriodBreakdownTable;
